import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import TrustPartner from "@/components/TrustPartner";
import SearchWidget from "@/components/SearchWidget";
import PropertyCard from "@/components/PropertyCard";
import ReviewsSection from "@/components/ReviewsSection";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { ShieldCheck, CreditCard, BadgeCheck, Headphones, MapPin, MousePointerClick, ArrowRight, TrendingUp, KeyRound, Building2, Star } from "lucide-react";

export const HERO_IMAGES = [
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_booking-nj-preview/artifacts/uqbgs8qm_498595032.webp",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_booking-nj-preview/artifacts/0et97wby_20220801_173251859_iOS.webp",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_booking-nj-preview/artifacts/7vnna410_db138fb72c850058e6041c468d6f8e7532ef64504511265f6b85e82fa830bc72.png",
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_booking-nj-preview/artifacts/odul5khh_IMG-20260511-WA0015.jpg",
];
const CONCIERGE = "https://images.pexels.com/photos/6474532/pexels-photo-6474532.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Home() {
  const { t, tc } = useI18n();
  const [props, setProps] = useState([]);
  const [hero, setHero] = useState(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/content/home_hero").then((r) => setHero(r.data?.value)).catch(() => {});
  }, []);

  const images = hero?.images?.length ? hero.images : HERO_IMAGES;

  useEffect(() => {
    const n = images.length || 1;
    const id = setInterval(() => setSlide((s) => (s + 1) % n), 5000);
    return () => clearInterval(id);
  }, [images.length]);

  const whyItems = [
    { icon: MousePointerClick, k: "direct" }, { icon: CreditCard, k: "secure" },
    { icon: BadgeCheck, k: "controlled" }, { icon: Headphones, k: "assist" },
    { icon: MapPin, k: "local" }, { icon: ShieldCheck, k: "simple" },
  ];

  return (
    <Layout transparentHeader>
      <Seo title="Conciergerie Airbnb à Rouen | Location courte durée | NJ Conciergerie"
        description="Conciergerie Airbnb à Rouen : NJ Conciergerie gère votre location courte durée de A à Z (Airbnb, Booking, ménage, linge). Côté voyageurs, réservez en direct nos logements à Rouen."
        canonical="/" jsonLd={useLocalBusinessLd()} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end">
        {images.map((src, i) => (
          <img key={src} src={src} alt="Logement NJ Conciergerie à Rouen"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === slide ? 1 : 0 }} loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"} decoding={i === 0 ? "sync" : "async"} />
        ))}
        <div className="absolute inset-0 nj-hero-overlay" />
        <div className="absolute bottom-6 right-6 z-10 flex gap-2" data-testid="hero-dots">
          {images.map((src, i) => (
            <button key={src} onClick={() => setSlide(i)} aria-label={`Photo ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === slide ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24 pt-32">
          <div className="max-w-3xl nj-fade-up">
            <span className="text-nj-gold text-xs md:text-sm tracking-[0.28em] uppercase font-semibold">Conciergerie · Rouen</span>
            <h1 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">
              Conciergerie Airbnb à Rouen — NJ Conciergerie
            </h1>
            <h2 className="font-serif font-light text-white/90 text-2xl sm:text-3xl mt-3">
              {tc(hero)?.title || t("home.searchTitle")}
            </h2>
            <p className="text-white/85 text-lg mt-5 max-w-xl leading-relaxed">
              {tc(hero)?.subtitle || "Des logements confortables, une conciergerie locale, une réservation directe et sécurisée."}
            </p>
          </div>
          <div className="mt-8 nj-fade-up" style={{ animationDelay: "0.15s" }}>
            <SearchWidget />
          </div>
          <div className="mt-4 nj-fade-up" style={{ animationDelay: "0.25s" }}>
            <span data-testid="hero-installment" className="inline-flex items-center gap-2 bg-white/90 backdrop-blur text-nj-primary text-sm font-medium px-4 py-2 rounded-full shadow-sm">
              <CreditCard className="w-4 h-4" /> Payez en 3 ou 4 fois avec Klarna
            </span>
          </div>
        </div>
      </section>

      {/* DEUX PARCOURS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="home-parcours">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Conciergerie Airbnb · Rouen</span>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-nj-text mt-3">Nous gérons votre location de A à Z</h2>
          <p className="text-nj-text2 mt-3">Airbnb · Booking · Tarification · Voyageurs · Ménage · Linge · Maintenance</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="bg-nj-primary text-white rounded-lg p-8 flex flex-col" data-testid="parcours-owner">
            <div className="flex items-center gap-3"><Building2 className="w-7 h-7 text-nj-gold" /><span className="text-nj-gold text-xs tracking-[0.24em] uppercase font-semibold">Vous êtes propriétaire</span></div>
            <h3 className="font-serif text-2xl md:text-3xl mt-4">Rentabilisez votre logement sans y penser</h3>
            <p className="text-white/75 mt-3 leading-relaxed flex-1">Estimation gratuite, mise en valeur, diffusion multi-plateformes et gestion complète. Vous percevez vos revenus, nous gérons le reste.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/estimation-revenus-airbnb-rouen" data-testid="home-estimate-btn" className="bg-nj-gold text-nj-dark font-semibold px-6 py-3 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Estimer mes revenus</Link>
              <Link to="/conciergerie-airbnb-rouen" className="border border-white/50 text-white font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors">Notre conciergerie</Link>
            </div>
          </div>
          <div className="bg-white border border-nj-olive rounded-lg p-8 flex flex-col" data-testid="parcours-traveler">
            <div className="flex items-center gap-3"><KeyRound className="w-7 h-7 text-nj-primary" /><span className="text-nj-primary text-xs tracking-[0.24em] uppercase font-semibold">Vous êtes voyageur</span></div>
            <h3 className="font-serif text-2xl md:text-3xl mt-4 text-nj-text">Séjournez à Rouen en toute sérénité</h3>
            <p className="text-nj-text2 mt-3 leading-relaxed flex-1">Des appartements et studios confortables, une réservation directe et sécurisée, une conciergerie locale à votre écoute.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/logements" className="bg-nj-primary text-white font-semibold px-6 py-3 rounded-sm hover:bg-nj-primaryHover transition-colors inline-flex items-center gap-2">Voir les logements <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/consigne" className="border border-nj-primary text-nj-primary font-semibold px-6 py-3 rounded-sm hover:bg-nj-olive transition-colors">Consigne à bagages</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PREUVES */}
      <section className="border-y border-nj-olive bg-nj-olive/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div><div className="font-serif text-3xl text-nj-primary flex items-center justify-center gap-1">4,5 <Star className="w-5 h-5 fill-nj-gold text-nj-gold" /></div><div className="text-nj-text2 text-sm mt-1">Note Google</div></div>
          <div><div className="font-serif text-3xl text-nj-primary">100% local</div><div className="text-nj-text2 text-sm mt-1">Équipe basée à Rouen</div></div>
          <div><div className="font-serif text-3xl text-nj-primary">Airbnb · Booking</div><div className="text-nj-text2 text-sm mt-1">Diffusion multi-plateformes</div></div>
          <div><div className="font-serif text-3xl text-nj-primary flex items-center justify-center gap-1"><Building2 className="w-6 h-6" /> Agence</div><div className="text-nj-text2 text-sm mt-1">Bd de la Marne, Rouen</div></div>
        </div>
      </section>

      <TrustPartner />

      {/* PROPERTIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="home-properties">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">{t("common.allProperties")}</span>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-nj-text mt-3">{t("home.propertiesTitle")}</h2>
            <p className="text-nj-text2 mt-3 max-w-xl">{t("home.propertiesSub")}</p>
          </div>
          <Link to="/logements" className="hidden md:inline-flex items-center gap-2 text-nj-primary font-medium hover:gap-3 transition-all">
            {t("common.seeAll")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {props.map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      </section>

      <ReviewsSection />

      {/* WHY */}
      <section className="bg-nj-primary text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">NJ Conciergerie</span>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl mt-3">{t("home.whyTitle")}</h2>
            <p className="text-white/70 mt-3 text-lg">{t("home.whySub")}</p>
          </div>
          <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 mt-14">
            {whyItems.map(({ icon: Icon, k }) => (
              <div key={k} data-testid={`why-${k}`} className="flex gap-4">
                <div className="shrink-0 w-12 h-12 rounded-sm bg-white/10 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-nj-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl">{t(`why.${k}`)}</h3>
                  <p className="text-white/65 text-sm mt-1.5 leading-relaxed">{t(`why.${k}D`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONCIERGE CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="overflow-hidden rounded-lg aspect-[4/3]">
            <img src={CONCIERGE} alt="Conciergerie" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div>
            <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Propriétaires</span>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-nj-text mt-3">Confiez-nous votre logement à Rouen</h2>
            <p className="text-nj-text2 mt-4 leading-relaxed">
              Création et optimisation d'annonces, photographies professionnelles, diffusion Airbnb & Booking,
              gestion des voyageurs, ménage et linge. Nous nous occupons de tout, vous percevez vos revenus.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/confier-mon-logement" data-testid="home-entrust-btn" className="bg-nj-primary text-white font-semibold px-6 py-3 rounded-sm hover:bg-nj-primaryHover transition-colors">Confier mon logement</Link>
              <Link to="/conciergerie" className="border border-nj-primary text-nj-primary font-semibold px-6 py-3 rounded-sm hover:bg-nj-olive transition-colors">Nos services</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
